import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess/acls.pb';
import { useQuery, useQueries } from 'react-query';
import { filterLocationIdsByACLs } from '@frontend/auth-helpers';
import { Options } from '@frontend/fetch';
import { LimitedSchemaQueryOptions } from '@frontend/react-query-helpers';
import { useAppFlagStore } from '@frontend/shared';
import { SchemaSettingsV1Service } from '../service';
import { GetReviewsSettingsIO } from '../types';

export const getGetReviewsSettingsQueryKey = (req: GetReviewsSettingsIO['input']) =>
  ['SettingsV1', 'GetReviewsSettings', req] as const;
type QueryKey = ReturnType<typeof getGetReviewsSettingsQueryKey>;

const useEnforceACLOnRequest = () => {
  const { getFeatureFlagValue } = useAppFlagStore();

  return (request: GetReviewsSettingsIO['input']): GetReviewsSettingsIO['input'] => {
    const locationIds = request.groupIds ?? (request.locationId ? [request.locationId] : []);
    const aclEnforcementEnabled = getFeatureFlagValue({
      flagName: 'nwx:reviews-fe-acl-enforcement',
      locationIds,
      strategy: 'ALL',
    });
    const locationIdsWithACL = aclEnforcementEnabled
      ? filterLocationIdsByACLs({
          locationIds,
          acls: Permission.REVIEW_SETTINGS,
        })
      : locationIds;

    return {
      ...request,
      locationId: request.groupIds ? locationIds[0] : request.locationId,
      groupIds: request.groupIds ? locationIdsWithACL : undefined,
    };
  };
};

/**
 * A hook that returns a query for the `GetReviewsSettings` endpoint.
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 */
export const useGetReviewsSettingsQuery = <E = unknown, D = GetReviewsSettingsIO['output']>(
  req: GetReviewsSettingsIO['input'],
  options?: LimitedSchemaQueryOptions<GetReviewsSettingsIO, E, D, QueryKey>,
  httpOptions?: Options
) => {
  const enforceACLOnRequest = useEnforceACLOnRequest();
  const newRequest = enforceACLOnRequest(req);

  return useQuery<GetReviewsSettingsIO['output'], E, D, QueryKey>({
    queryKey: getGetReviewsSettingsQueryKey(newRequest),
    queryFn: () => SchemaSettingsV1Service.GetReviewsSettings(newRequest, httpOptions),
    ...options,
    enabled: (options?.enabled ?? true) && (!!newRequest.groupIds?.length || !!newRequest.locationId),
  });
};

/**
 * A hook that returns an array of queries for the `GetReviewsSettings` endpoint.
 * It takes an array of objects with the following properties:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 */
export const useGetReviewsSettingsQueries = <E = unknown, D = GetReviewsSettingsIO['output']>(
  argsArr: {
    req: GetReviewsSettingsIO['input'];
    options?: LimitedSchemaQueryOptions<GetReviewsSettingsIO, E, D, QueryKey>;
    httpOptions?: Options;
  }[]
) => {
  const enforceACLOnRequest = useEnforceACLOnRequest();

  return useQueries(
    argsArr.map(({ req, options, httpOptions }) => {
      const newRequest = enforceACLOnRequest(req);
      return {
        queryKey: getGetReviewsSettingsQueryKey(newRequest),
        queryFn: () => SchemaSettingsV1Service.GetReviewsSettings(newRequest, httpOptions),
        ...options,
        enabled: (options?.enabled ?? true) && (!!newRequest.groupIds?.length || !!newRequest.locationId),
      };
    })
  ) as ReturnType<typeof useGetReviewsSettingsQuery<E, D>>[];
};
